import { Injectable } from '@angular/core';
import CryptoJS from 'crypto-js';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { gptModel } from '../enum/gpt-model';
import { HttpService } from './http.service';
import { SessionService } from './session-service.service';

@Injectable({
  providedIn: 'root',
})
export class OpenAIService {
  constructor(
    private http: HttpService,
    private sessionService: SessionService,
  ) {}

  async gptFourModel(Messages) {
    try {
      const body = {
        model: gptModel.gpt_4,
        temperature: 0.7,
        top_p: 1,
        presence_penalty: 0.0,
        messages: Messages,
      };
      const response = await firstValueFrom(this.http.post<any>(`https://api.openai.com/v1/chat/completions`, body));

      return response.choices[0].message;
    } catch (error) {
      throw error;
    }
  }

  async openAIEncryptionAPICall(prompt: string, assistantRes?: string, openAiModel?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        prompt: prompt,
        assistant: assistantRes ? assistantRes : '',
        model: openAiModel ? openAiModel : gptModel.gpt_4o_mini,
      };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-lesson-plan`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async openAIEncryptionCallForGenerateSolutions(question?: string, stream = false, openAiModel?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        question,
        model: openAiModel ? openAiModel : gptModel.gpt_4o_mini,
        stream,
      };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      if (!stream) {
        const response = await firstValueFrom(
          this.http.post<any>(`${environment.NODE_SERVER}/model/generate-solution`, encryptedData),
        );

        return response;
      } else {
        return new Observable<any>((observer) => {
          fetch(`${environment.NODE_SERVER}/model/generate-solution`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(encryptedData),
          })
            .then((response) => {
              const reader = response.body.getReader();
              const decoder = new TextDecoder('utf-8');

              function read() {
                reader.read().then(({ done, value }) => {
                  if (done) {
                    observer.complete();
                    return;
                  }
                  const chunk = decoder.decode(value, { stream: true });

                  observer.next(chunk);
                  read();
                });
              }

              read(); // Start reading
            })
            .catch((error) => {
              observer.error(error);
            });
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async openAIEncryptionAPICallForPacingPlans(messages) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);
      const body = { messages };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-pacing-plan`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async gptFourModelForCreateQuestions(prompt: string) {
    try {
      const body = {
        model: gptModel.gpt_4,
        max_tokens: 7000,
        temperature: 0.7,
        top_p: 1,
        presence_penalty: 0.0,
        messages: [
          {
            role: 'system',
            content: 'You are a helpful teacher assistant that creates better questions answers of questions',
          },
          {
            role: 'user',
            content: `${prompt}`,
          },
        ],
      };
      const response = await firstValueFrom(this.http.post<any>(`https://api.openai.com/v1/chat/completions`, body));

      return response.choices[0].message;
    } catch (error) {
      throw error;
    }
  }

  async openAIEncryptionGenerateQuestions(prompt: string, openAiModel?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        prompt: prompt,
        model: openAiModel ? openAiModel : gptModel.gpt_4o_mini,
      };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-questions`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async openAIEncryptionMCQAnswers(prompt: string, openAiModel?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        prompt: prompt,
        model: openAiModel ? openAiModel : gptModel.gpt_4o_mini,
      };

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-mcq-answer`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }
  async openAIEncryptionGenerateAnswers(prompt: string, model?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);
      const body = JSON.stringify({ prompt, model });
      const encryptedData = {
        params: CryptoJS.AES.encrypt(body, derivedKey).toString(),
        sessionToken: token,
      };

      return new Observable<any>((observer) => {
        fetch(`${environment.NODE_SERVER}/model/generate-answers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }
                const chunk = decoder.decode(value, { stream: true });

                observer.next(chunk);
                read();
              });
            }

            read(); // Start reading
          })
          .catch((error) => {
            observer.error(error);
          });
      });
    } catch (error) {
      throw error;
    }
  }

  async streamCompletion(model: string, prompt: string): Promise<Observable<string>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify({ model, prompt }), derivedKey).toString(),
      sessionToken: token,
    };

    return new Observable<string>((observer) => {
      fetch(`${environment.NODE_SERVER}/model/generate-presentation-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(encryptedData),
      })
        .then((response) => {
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');

          function read() {
            reader.read().then(({ done, value }) => {
              if (done) {
                observer.complete();
                return;
              }
              const chunk = decoder.decode(value, { stream: true });

              observer.next(chunk);
              read();
            });
          }

          read(); // Start reading
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  async checkAnswerCorrectNess(prompt: string, openAiModel?: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        prompt: prompt,
        model: openAiModel ? openAiModel : gptModel.gpt_4o_mini,
      };
      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/check-answer`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async createGlobalPacingPlans(prompt: string) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      const body = {
        prompt,
      };

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-global-pacing-plans`, encryptedData),
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  async generatePacingPlansWithAssistant(body: any) {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };

      const response = await firstValueFrom(
        this.http.post<any>(`${environment.NODE_SERVER}/model/generate-assistant-pacing-plans`, encryptedData),
      );

      return response;
    } catch (error) {
      // console.log(error);
    }
  }

  async getOverallSummaryForAssignment(body): Promise<Observable<string>> {
    try {
      // Fetch session token first
      const { token } = await this.sessionService.getSessionToken();
      const derivedKey = this.sessionService.extractDerivedKey(token);

      // Encrypt with derived key + attach token
      const encryptedData = {
        params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
        sessionToken: token,
      };
      return new Observable<string>((observer) => {
        try {
          fetch(`${environment.NODE_SERVER}/model-assistant-route/generate_summary`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(encryptedData),
          })
            .then((response) => {
              if (!response.body) {
                throw new Error('Readable stream not supported in response.');
              }

              const reader = response.body.getReader();
              const decoder = new TextDecoder('utf-8');

              function read() {
                reader.read().then(({ done, value }) => {
                  if (done) {
                    observer.complete();
                    return;
                  }

                  // Convert chunk to string
                  const chunkString = decoder.decode(value, { stream: true });

                  // Split into lines in case multiple messages are received
                  const chunkLines = chunkString.trim().split('\n');

                  chunkLines.forEach((line) => {
                    try {
                      // Remove 'data: ' prefix if it exists
                      if (line.startsWith('data: ')) {
                        line = line.replace('data: ', '').trim();
                      }

                      // Ensure the line is valid JSON before parsing
                      if (line) {
                        const parsedData = JSON.parse(line); // Convert to JSON
                        if (parsedData.text) {
                          observer.next(parsedData.text); // Emit only the "text" value
                        }
                      }
                    } catch (e) {
                      console.warn('Error parsing JSON chunk:', line, e);
                    }
                  });

                  read(); // Continue reading
                });
              }

              read(); // Start reading
            })
            .catch((error) => {
              observer.error(error);
            });
        } catch (error) {
          observer.error(error);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async getOverallSummaryForAssessment(body): Promise<Observable<string>> {
    // Fetch session token first
    const { token } = await this.sessionService.getSessionToken();
    const derivedKey = this.sessionService.extractDerivedKey(token);

    // Encrypt with derived key + attach token
    const encryptedData = {
      params: CryptoJS.AES.encrypt(JSON.stringify(body), derivedKey).toString(),
      sessionToken: token,
    };

    return new Observable<string>((observer) => {
      try {
        fetch(`${environment.NODE_SERVER}/model-assistant-route/generate_summary_for_assessment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(encryptedData),
        })
          .then((response) => {
            if (!response.body) {
              throw new Error('Readable stream not supported in response.');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            function read() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  observer.complete();
                  return;
                }

                // Convert chunk to string
                const chunkString = decoder.decode(value, { stream: true });

                // Split into lines in case multiple messages are received
                const chunkLines = chunkString.trim().split('\n');

                chunkLines.forEach((line) => {
                  try {
                    // Remove 'data: ' prefix if it exists
                    if (line.startsWith('data: ')) {
                      line = line.replace('data: ', '').trim();
                    }

                    // Ensure the line is valid JSON before parsing
                    if (line) {
                      const parsedData = JSON.parse(line); // Convert to JSON
                      if (parsedData.text) {
                        observer.next(parsedData.text); // Emit only the "text" value
                      }
                    }
                  } catch (e) {
                    console.warn('Error parsing JSON chunk:', line, e);
                  }
                });

                read(); // Continue reading
              });
            }

            read(); // Start reading
          })
          .catch((error) => {
            observer.error(error);
          });
      } catch (error) {
        observer.error(error);
      }
    });
  }
}
